/**
 * useAuth Hook
 * Custom hook for managing authentication state and initialization
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { apiService } from '../services/api';
import { useTaskProcessingStore } from '@stores/taskProcessing.store';
import type { TaskProcessingStore } from '@stores/taskProcessing.types';

// ============================================================================
// Selectors (extracted to prevent re-creation on each render)
// ============================================================================

const selectClearTaskProcessingStore = (state: TaskProcessingStore) =>
  state.clearAll;

// ============================================================================
// Hook Implementation
// ============================================================================

export const useAuth = () => {
  const {
    user,
    profile,
    isAuthenticated,
    isLoading,
    error,
    setUser,
    setProfile,
    setLoading,
    setError,
    logout: storeLogout,
    clearError,
  } = useAuthStore();

  const clearTaskProcessingStore = useTaskProcessingStore(
    selectClearTaskProcessingStore,
  );

  // Enhanced logout function that clears server session
  const logout = useCallback(async () => {
    try {
      await apiService.logout();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Logout API call failed:', error);
      // Continue with local logout even if API call fails
    } finally {
      storeLogout();
      clearTaskProcessingStore();
    }
  }, [clearTaskProcessingStore, storeLogout]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const userData = await apiService.me();
      setUser(userData);
      return userData;
    } catch (error) {
      setUser(null);
      throw error;
    }
  }, [setUser]);

  // Refresh profile data
  const refreshProfile = useCallback(async () => {
    try {
      const profileData = await apiService.getRestaurantProfile();
      setProfile(profileData);
      return profileData;
    } catch (error) {
      setProfile(null);
      throw error;
    }
  }, [setProfile]);

  // Check if user has completed profile setup
  const hasCompletedProfile = useMemo(() => !!profile, [profile]);
  const isEmailVerified = useMemo(() => user?.verified ?? false, [user]);

  const value = useMemo(
    () => ({
      user,
      profile,
      isAuthenticated,
      isLoading,
      error,
      hasCompletedProfile,
      isEmailVerified,
      logout,
      refreshUser,
      refreshProfile,
      clearError,
      clearTaskProcessingStore,
      setUser,
      setProfile,
      setLoading,
      setError,
    }),
    [
      clearError,
      clearTaskProcessingStore,
      error,
      hasCompletedProfile,
      isAuthenticated,
      isEmailVerified,
      isLoading,
      logout,
      profile,
      refreshProfile,
      refreshUser,
      setError,
      setLoading,
      setProfile,
      setUser,
      user,
    ],
  );

  return value;
};

export default useAuth;

interface UseAuthInitializationOptions {
  enabled?: boolean;
}

export const useAuthInitialization = (
  options: UseAuthInitializationOptions = {},
) => {
  const { enabled = true } = options;
  const { setLoading, setError, setUser, setProfile } = useAuthStore();
  const initializedRef = useRef(false);

  useEffect(() => {
    // Prevent re-initialization if already complete
    if (!enabled || initializedRef.current) {
      return;
    }

    let isActive = true;

    const initialize = async () => {
      // Mark as initialized immediately to prevent concurrent runs
      initializedRef.current = true;
      setLoading(true);
      setError(null);

      try {
        await apiService.csrf();

        const userData = await apiService.me();
        if (!isActive) return;

        setUser(userData);

        // Always attempt to fetch profile (don't check local state)
        try {
          const profileData = await apiService.getRestaurantProfile();
          if (!isActive) return;
          setProfile(profileData);
        } catch (profileError) {
          void profileError;
          if (!isActive) return;
          setProfile(null);
        }
      } catch (error) {
        if (!isActive) return;
        setUser(null);
        setProfile(null);
      } finally {
        if (!isActive) return;
        setLoading(false);
      }
    };

    void initialize();

    return () => {
      isActive = false;
      // React StrictMode immediately re-runs effects in development. Allow the
      // replacement effect to initialize; the cancelled request remains gated
      // by isActive and cannot update the store.
      initializedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]); // Only re-run if enabled changes, NOT on store action changes
};
